import { useCurrentSiteId } from '@hotjar/site';
import { useMemo } from 'react';
import type { QueryStatus } from 'react-query';
import type {
  ReactionStyle,
  ReactionQuestion,
} from '@hotjar/feedback/domain/Surveys/Questions/SurveyQuestion';
import { useSurveyQuestionsQuery } from '../../domain/Surveys/query';
import type { SurveyQuestion } from '../../domain/Surveys/Questions';
import { QuestionTypes } from '../../domain/Surveys/Questions';

interface UseSurveyQuestions {
  questions: Array<SurveyQuestion> | undefined;
  hasAnyStatements: boolean;
  hasAnyKeywords: boolean;
  hasAnyRatings: boolean;
  questionsStatus: QueryStatus;
  firstRatingStyle: ReactionStyle;
}

export const useSurveyQuestions = (surveyId: number | undefined): UseSurveyQuestions => {
  const siteId = useCurrentSiteId();
  const { data: questions, status: questionsStatus } = useSurveyQuestionsQuery(
    siteId ? Number(siteId) : undefined,
    surveyId,
  );

  const hasAnyStatements = useMemo(() => {
    return (
      questions !== undefined &&
      questions.some((q) => q.type === QuestionTypes.TITLE_AND_DESCRIPTION && !q.deleted)
    );
  }, [questions]);

  const hasAnyKeywords = useMemo(() => {
    return (
      questions !== undefined &&
      questions.some((q) => {
        if (q.deleted) return false;
        switch (q.type) {
          case QuestionTypes.EMAIL:
          case QuestionTypes.SINGLE_OPEN_ENDED_SINGLE_LINE:
          case QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE:
          case QuestionTypes.SINGLE_CLOSE_ENDED:
          case QuestionTypes.MULTIPLE_CLOSE_ENDED:
            return true;
          default:
            return false;
        }
      })
    );
  }, [questions]);

  const hasAnyRatings = useMemo(() => {
    return (
      questions !== undefined &&
      questions.some((q) => {
        return q.type === QuestionTypes.REACTION;
      })
    );
  }, [questions]);

  const firstRatingStyle = useMemo(() => {
    let style: ReactionStyle = 'default';
    if (hasAnyRatings) {
      const reactionQuestion = questions?.find((q) => {
        return q.type === QuestionTypes.REACTION;
      }) as ReactionQuestion | undefined;

      style = reactionQuestion?.reaction_style || style;
    }
    return style;
  }, [hasAnyRatings, questions]);

  return {
    questions,
    hasAnyStatements,
    hasAnyKeywords,
    questionsStatus,
    hasAnyRatings,
    firstRatingStyle,
  };
};
